import { Component } from "react";

import { cn } from "@btnet/utilities-core";

import { friendlyDefaultDisplayValue } from "utilities/helpers";
import { isNullOrWhitespace } from "utilities/string/string";

import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import {
    type AddressEntity,
    AddressFieldTypes,
    getAddressMapUrl,
    getAddressPlainText,
    getPinTypeForMap,
} from "commonComponents/entity/address/Address/Address.api.types";
import AddressMapButton from "commonComponents/entity/address/Address/AddressMapButton";
import { type IMapPosition, PinTypes } from "commonComponents/entity/map/Map.types";
import { AddressActionSheet } from "commonComponents/utilities/AddressActionSheet/AddressActionSheet";
import TextTruncate from "commonComponents/utilities/TextTruncate/TextTruncate";

import "./AddressDisplay.less";

interface IAddressDisplayProps {
    address: AddressEntity;
    className?: string;
    singleLineFormat: boolean;
    emptyState?: React.ReactNode;
    showMapIcon?: boolean;
    /**
     * Wraps the address in a link that opens the address in Google Maps.
     * Defaults to `true` so addresses are consistently tappable across the app;
     * pass `false` in print, embed, or read-only-summary contexts where a maps
     * link doesn't belong.
     */
    showMapsLink?: boolean;
    mapPinTooltipHeader?: string;
    mapPinTooltipBody?: string;
    mapDefaultStartPosition?: IMapPosition;
    entityName?: string;
    testId?: string;
    /**
     * Opt in to the redesigned BDS address layout. Defaults to `false` so the
     * legacy layout stays the default; consumers that want the new render pass
     * `useBds` explicitly (the same opt-in `Address` exposes).
     */
    useBds?: boolean;
}

class AddressDisplay extends Component<IAddressDisplayProps> {
    static defaultProps = {
        singleLineFormat: false,
        showMapIcon: true,
        showMapsLink: true,
    };

    /* For formatting cultures with different address line structures with delimiter
     */
    private formatDelimiter = (
        leftSide: string | undefined,
        delimiter: string,
        rightSide: string | undefined,
    ) => {
        if (
            leftSide &&
            rightSide &&
            !isNullOrWhitespace(leftSide) &&
            !isNullOrWhitespace(rightSide)
        ) {
            return leftSide.concat(delimiter.concat(rightSide));
        }
        return (leftSide || "").concat(rightSide || "");
    };

    private formatAddress = () => {
        const {
            address,
            singleLineFormat,
            mapPinTooltipBody,
            mapPinTooltipHeader,
            entityName,
            mapDefaultStartPosition,
            showMapIcon,
            showMapsLink,
            useBds,
        } = this.props;

        const street = address.fields.find(
            (field) => field.addressFieldType === AddressFieldTypes.Street,
        );
        const city = address.fields.find(
            (field) => field.addressFieldType === AddressFieldTypes.City,
        );
        const suburb = address.fields.find(
            (field) => field.addressFieldType === AddressFieldTypes.Suburb,
        );
        const state = address.fields.find(
            (field) => field.addressFieldType === AddressFieldTypes.State,
        );
        const zip = address.fields.find(
            (field) => field.addressFieldType === AddressFieldTypes.Zip,
        );
        const country =
            address.countryDropdown &&
            address.countryDropdown.find(
                (country) => parseInt(country.id) === address.countryDropdownId,
            );

        const hasStreet = street && street.value;
        const hasCity = city && city.value;
        const hasState = state && state.value;
        const hasSuburb = suburb && suburb.value;
        const hasZip = zip && zip.value;
        const hasCountry = country && country.title;
        const isUK = address.globalCountry && address.globalCountry === "GB";
        const isIE = address.globalCountry && address.globalCountry === "IE";

        if (!hasStreet && !hasCity && !hasState && !hasSuburb && !hasZip) {
            return this.props.emptyState || friendlyDefaultDisplayValue;
        }

        const mapsUrl = showMapsLink ? getAddressMapUrl(address) : undefined;
        const pinType = address.location ? getPinTypeForMap(address.location) : undefined;
        const showPinMapIcon =
            showMapIcon && !mapsUrl && address.location && pinType !== PinTypes.None;

        if (singleLineFormat) {
            return (
                <TextTruncate>
                    {/* TODO: this is just implementing the default country (US). Please update formatting of specific cultures */}
                    {this.formatDelimiter(
                        this.formatDelimiter(
                            this.formatDelimiter(hasStreet, ", ", hasCity),
                            ", ",
                            hasState,
                        ),
                        " ",
                        hasZip,
                    )}
                </TextTruncate>
            );
        } else if (useBds) {
            return (
                <>
                    <div className="flex flex-row AddressTopLine AddressLine">
                        {hasStreet ? `${street.value}` : ""}
                        {showPinMapIcon && (
                            <span className="padding-left-xs">
                                <AddressMapButton
                                    mapPinTooltipHeader={mapPinTooltipHeader}
                                    mapPinTooltipBody={mapPinTooltipBody}
                                    mapDefaultStartPosition={mapDefaultStartPosition}
                                    entityName={entityName}
                                    address={address}
                                    location={address.location}
                                    addressId=""
                                    readOnly
                                />
                            </span>
                        )}
                    </div>
                    {!(isUK || isIE) && (
                        <>
                            <div className="flex flex-row AddressLine">
                                {hasSuburb ? `${suburb.value}` : ""}
                            </div>
                            <div className="flex flex-row AddressLine">
                                {hasCity ? `${city.value}` : ""}
                                {hasState ? `${hasCity ? ", " : ""}${state.value}` : ""}
                                {hasZip
                                    ? `${hasState ? " " : hasCity ? ", " : ""}${zip.value}`
                                    : ""}
                            </div>
                        </>
                    )}
                    {(isUK || isIE) && (
                        <>
                            <div className="flex flex-row AddressLine">
                                {hasCity ? `${city.value}` : ""}
                            </div>
                            <div className="flex flex-row AddressLine">
                                {hasSuburb ? `${suburb.value}` : ""}
                            </div>
                            <div className="flex flex-row AddressLine">
                                {hasZip ? `${zip.value}` : ""}
                            </div>
                        </>
                    )}

                    <div className="flex flex-row AddressLine">
                        {hasCountry ? `${country.title}` : ""}
                    </div>
                </>
            );
        } else {
            return (
                <>
                    <BTRow className="AddressTopLine AddressLine" align="middle">
                        {hasStreet ? `${street.value}` : ""}
                        {showPinMapIcon && (
                            <span className="padding-left-xs">
                                <AddressMapButton
                                    mapPinTooltipHeader={mapPinTooltipHeader}
                                    mapPinTooltipBody={mapPinTooltipBody}
                                    mapDefaultStartPosition={mapDefaultStartPosition}
                                    entityName={entityName}
                                    address={address}
                                    location={address.location}
                                    addressId=""
                                    readOnly
                                />
                            </span>
                        )}
                    </BTRow>
                    {!(isUK || isIE) && (
                        <>
                            <BTRow className="AddressLine">
                                {hasSuburb ? `${suburb.value}` : ""}
                            </BTRow>
                            <BTRow className="AddressLine">
                                {hasCity ? `${city.value}` : ""}
                                {hasState ? `${hasCity ? ", " : ""}${state.value}` : ""}
                                {hasZip
                                    ? `${hasState ? " " : hasCity ? ", " : ""}${zip.value}`
                                    : ""}
                            </BTRow>
                        </>
                    )}
                    {(isUK || isIE) && (
                        <>
                            <BTRow className="AddressLine">{hasCity ? `${city.value}` : ""}</BTRow>
                            <BTRow className="AddressLine">
                                {hasSuburb ? `${suburb.value}` : ""}
                            </BTRow>
                            <BTRow className="AddressLine">{hasZip ? `${zip.value}` : ""}</BTRow>
                        </>
                    )}

                    <BTRow className="AddressLine">{hasCountry ? `${country.title}` : ""}</BTRow>
                </>
            );
        }
    };

    render() {
        const { testId = "addressDisplay" } = this.props;
        const mapsUrl = this.props.showMapsLink ? getAddressMapUrl(this.props.address) : undefined;
        return (
            <div className={cn("AddressDisplay", this.props.className)} data-testid={testId}>
                {mapsUrl ? (
                    <AddressActionSheet
                        copyText={getAddressPlainText(this.props.address)}
                        mapAction={{ kind: "url", mapsUrl }}
                        testIdBase="addressMapsLink"
                    >
                        {this.formatAddress()}
                    </AddressActionSheet>
                ) : (
                    this.formatAddress()
                )}
            </div>
        );
    }
}
export default AddressDisplay;
